/**
 * The only module in this package that calls `fetch`.
 *
 * That is a rule, not a habit, and `api.test.ts` enforces it by reading the
 * package's own sources: it is the single place a 401 can be turned into "your
 * session ended", the single place the session cookie's `credentials` mode is
 * decided, and the single place a credential refusal is turned into words. A
 * screen that fetched for itself would handle each of those differently, or —
 * far more likely — not at all.
 *
 * **The session cookie is `HttpOnly`.** This package cannot read it and must
 * not try. "Am I signed in" is answered by `GET /api/auth/me` returning 200 or
 * 401, never by looking for a cookie, and `api.test.ts` reads this package's
 * sources to assert that nothing here reaches for one. (That check greps, and
 * greps comments too — the same deliberate bluntness `check:boundary` has — so
 * this paragraph is careful not to spell the property it forbids.)
 *
 * **URLs are resolved against `window.location.origin` explicitly** rather than
 * handed to `fetch` as relative paths. A browser would resolve them itself, but
 * Node's `fetch` — which is what the test environment gets, because jsdom does
 * not implement `fetch` at all — throws `Failed to parse URL` on a relative
 * one. Resolving here means the tests exercise the same code path the browser
 * does instead of a stub that could not fail the same way. The SPA and the API
 * are served from one origin (`http/static.ts` in dev, Vite's proxy otherwise)
 * precisely so this holds and the cookie needs no CORS treatment.
 */

/** The four roles that can sign in. Mirrors `packages/server/src/auth/users.ts`. */
export type Role = 'rep' | 'program_manager' | 'analyst' | 'ops'

export interface User {
  id: string
  email: string
  displayName: string
  role: Role
}

/**
 * The one thing a rep is told when a sign-in is refused, whatever the reason.
 *
 * The server answers a wrong password, an unknown email and a deactivated
 * account with byte-identical 401s, and burns a dummy scrypt hash on the
 * unknown-email path so that even the timings match (`auth/users.ts`). All of
 * that work exists to stop this app being an oracle for "does this person have
 * an account here", and a client that renders "no account with that address"
 * for one of the three gives it back. So there is exactly one message, and
 * `login` below branches on nothing but "was this a refusal at all".
 */
export const CREDENTIALS_REJECTED = 'That email and password did not match.'

/** What a 401 means on every route except login: the session is gone. */
export const SESSION_ENDED = 'Your session has ended. Sign in again.'

/**
 * No response at all. Kept distinct from `CREDENTIALS_REJECTED` on purpose: a
 * server that could not be reached said nothing about the credentials, and
 * telling a rep their password was wrong when it was not sends them to reset a
 * password that was right all along. The distinction discloses nothing about
 * any account, which is why it is allowed to exist.
 */
export const NETWORK_UNREACHABLE = 'Could not reach the server. Check your connection and try again.'

/** A 4xx that is not a 401 — the request itself was refused. */
export const REQUEST_REFUSED = 'The server refused that request.'

/** A 5xx, or a response that was not the JSON it claimed to be. */
export const SERVER_TROUBLE = 'The server had a problem. Try again in a moment.'

/** What went wrong, in the terms a screen has to make a decision about. */
export type ApiFailure = 'session_ended' | 'not_found' | 'refused' | 'server' | 'network'

/**
 * Every non-success this client raises. `message` is copy a screen may render
 * as-is; `code` is the server's own machine-readable `error` field (e.g.
 * `no_completed_run`) and `detail` its developer-facing `message`, for screens
 * that need to tell two 404s apart.
 */
export class ApiError extends Error {
  readonly failure: ApiFailure
  readonly status: number | null
  readonly code: string | null
  readonly detail: string | null
  /**
   * The server's own `errors: string[]`, when it sent one — the named reasons a
   * refusal was a refusal. Empty on every response that carried none, so a
   * screen may render it directly and never has to distinguish "no list" from
   * "an empty list".
   *
   * It is separate from `detail` because it answers a different question. A
   * screen showing one sentence wants `detail`; a screen showing a list of
   * fields to go and fix wants this. `ConfigConsole.tsx` renders it as the
   * `<ul>` its commit-problem block has always drawn — the block was built for
   * a list and, until this field existed, was only ever handed one item, and
   * that item was the generic fallback.
   */
  readonly reasons: readonly string[]

  constructor(
    message: string,
    failure: ApiFailure,
    status: number | null,
    code: string | null,
    detail: string | null,
    options?: { cause?: unknown; reasons?: readonly string[] },
  ) {
    super(message, options)
    this.name = 'ApiError'
    this.failure = failure
    this.status = status
    this.code = code
    this.detail = detail
    this.reasons = options?.reasons ?? []
  }

  /** The one a screen almost always wants: stop rendering, ask them to sign in. */
  get sessionEnded(): boolean {
    return this.failure === 'session_ended'
  }
}

export type LoginResult = { ok: true; user: User } | { ok: false; message: string }

/**
 * A JSON value, for the parts of a payload this client does not yet describe.
 *
 * Deliberate, and narrower than it looks. Every shape this client *sends* is
 * typed exactly, because that is where a client can be wrong in a way the
 * server punishes with a 400 (`GET /api/accounts` rejects an unrecognised
 * query parameter rather than ignoring it, and the scenario route rejects an
 * unrecognised override key). The large read payloads — an account's estimate,
 * a derivation, a scenario result — are the engine's own `EstimateResult` and
 * friends, several hundred lines of type each, and this package cannot import
 * them: it has no `@types/node` and no reference to the server project, on
 * purpose. Hand-transcribing them here would produce a type nothing checks
 * against the wire, which is worse than an honest `JsonValue`, so each screen
 * task narrows the part it renders as it lands.
 */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
export type JsonObject = { [key: string]: JsonValue }

/**
 * One row of the ranked targeting list.
 *
 * Transcribed from `AccountListRow` in `packages/server/src/query/accounts.ts`.
 * It is flat and stable, which is why it is written out here when the deeper
 * payloads are not — but it is a transcription, so Task 12 (the first screen to
 * render it) should check it against a live payload rather than trust it.
 *
 * Note what is **not** here: any weighting arithmetic. The server computes
 * `curtailableKwWeighted` from `config.ranking.confidenceWeights`, and the SPA
 * never multiplies anything (the mock does, with the wrong weights).
 */
export interface AccountRow {
  accountId: string
  accountNumber: string
  name: string
  naics: string
  naicsLabel: string
  enrollmentStatus: 'enrolled' | 'not_enrolled' | 'declined'
  eligibilityStatus: 'in_scope' | 'out_of_scope'
  estimateId: string
  confidence: ConfidenceLabel
  curtailableKwRaw: number
  curtailableKwWeighted: number
  baselinePeakKw: number | null
  sizeBand: number | null
  hasDecomposition: boolean
  buildingType: string | null
  characterized: boolean
  flagCodes: string[]
  internalFlagCodes: string[]
  cohortOutlier: boolean
  priorSeasonOutcome: 'enrolled' | 'declined' | 'no_answer' | 'met_follow_up' | null
  lastDeclinedAt: string | null
  hasNotes: boolean
  /**
   * How many non-redacted notes the account carries (spec §7.7). The server
   * derives `hasNotes` from this same number, so the two cannot disagree —
   * `query/accounts.ts` says why, and this package must not recompute either
   * one from the other.
   */
  noteCount: number
  mostRecentNoteAt: string | null
  bindingHour: number
  seasonValueTotal: number
}

export type ConfidenceLabel = 'high' | 'medium' | 'low'

export interface AccountsPage {
  runId: string
  total: number
  accounts: AccountRow[]
}

/**
 * The filters `GET /api/accounts` recognises, and nothing else.
 *
 * The route answers 400 to an unrecognised parameter so that a caller can never
 * believe a filter took effect when it did not; this type is that guarantee's
 * client-side half, and `serialise` below writes out only the keys present on
 * it.
 */
export type AccountFilters = {
  eligibilityStatus?: 'in_scope' | 'out_of_scope'
  enrollmentStatus?: 'enrolled' | 'not_enrolled' | 'declined'
  confidence?: ConfidenceLabel
  sizeBand?: number
  naics?: string
  buildingType?: string
  minCurtailableKw?: number
  hasFlags?: boolean
  savedListId?: string
  priorSeasonOutcome?: 'enrolled' | 'declined' | 'no_answer' | 'met_follow_up'
  excludeDeclinedOnOrAfter?: string
  hasNotes?: boolean
  sortBy?: 'rank' | 'noteRecency'
  /** Opt out of the program's decline suppression (R-16 AC-3). Not a filter. */
  includeDeclined?: boolean
}

/**
 * **`GET /api/accounts/:id` now takes no options at all, and that is the third
 * time this type has been narrowed rather than grown.**
 *
 * `?mode=presentation` left in Task 18 and `?asOf` in Task 19, both for the
 * same reason: neither is an *option* on this request, because neither answers
 * with an `AccountDetail`. `?mode` answers with the customer projection
 * (`api.presentation`) and `?asOf` answers with `RecomputedPayload`
 * (`api.recompute`) — an envelope with no `characterization`, no `note`, no
 * `estimate.id` and no `eventWindow`, every one of which reads `undefined` at
 * runtime through a type that says otherwise and none of which is a compile
 * error.
 *
 * One route, three payloads, three calls. The split is also what makes the
 * combination the server refuses — `?asOf` with `?mode=presentation`, a 400,
 * because the customer payload has no field for "not the estimate of record"
 * (display rule 2) — unspellable here rather than a runtime 400.
 */

export type DerivationAspect = 'baseline' | 'factors' | 'sustain' | 'config'

/**
 * What every derivation carries: which figure, on which run, it is the working
 * for. Transcribed from `DerivationEnvelope` in
 * `packages/server/src/http/routes/derivation.ts`.
 */
export interface DerivationEnvelope {
  accountId: string
  aspect: DerivationAspect
  runId: string
  estimateId: string
  asOfDate: string
}

/**
 * The day-of adjustment as the *stored* estimate reports it.
 *
 * **`factor` is 1 on all 313 accounts of this book, and that is correct.** The
 * adjustment is a settlement-time correction against a called event's actual
 * pre-window load; a forward-looking targeting estimate has no event day to
 * measure. The cap is on the wire anyway because it is part of the method the
 * figure was produced under — not because anything applied it. Anything drawing
 * this must say which of the two it is.
 */
export interface BaselineAdjustment {
  enabled: boolean
  capFraction: number
  lowerBound: number
  upperBound: number
  factor: number
  /** `null` unless `factor` sits exactly on a bound. Never "was capped" — see the server. */
  atBound: 'lower' | 'upper' | null
}

interface BaselineCommon extends DerivationEnvelope {
  aspect: 'baseline'
  method: string
  lookbackDays: number
  excludeWeekends: boolean
  excludePriorEventDays: boolean
  flags: EstimateFlag[]
}

/**
 * `available: false` is a scored estimate for which no comparable day survived
 * selection (R-2: nothing fabricates a baseline). The day list and the peak are
 * **absent**, not zeroed, which is why this is a union rather than a bag of
 * nullable fields — a screen cannot print `0.0 kW peak` by accident.
 */
export type BaselineDerivation =
  | (BaselineCommon & {
    available: true
    /** The dates themselves, ascending — a regulator audits which days were averaged. */
    comparableDays: string[]
    adjustment: BaselineAdjustment
    peakKw: number
    baseLoadKw: number
  })
  | (BaselineCommon & { available: false })

/** Which tier of the factor table `resolveFactor` selected. */
export type FactorBasis = 'industry_and_building_type' | 'industry_only' | 'default' | 'none'

/**
 * Display rule 3's three states, kept apart on the wire so they cannot collapse
 * into each other on the way to a screen.
 */
export type CharacterizationState = 'recorded' | 'undetermined' | 'uncharacterized'

export interface GoverningFactorRow {
  endUse: string
  basis: FactorBasis
  /** The matched row's own key; `null` in either position is that tier's wildcard. */
  naics: string | null
  buildingType: string | null
  shedFraction: number
  shiftFraction: number
  /** What this end use contributed to the window, or `null` when the mix never reached it. */
  appliedKw: number | null
  share: number | null
}

export interface FactorsDerivation extends DerivationEnvelope {
  aspect: 'factors'
  key: {
    naics: string
    naicsLabel: string
    buildingType: string | null
    characterizationState: CharacterizationState
    characterizationVersion: number | null
    configVersionId: string
  }
  /** The most specific tier any row reached. `rows` is the derivation; this is its headline. */
  basis: FactorBasis
  rows: GoverningFactorRow[]
}

export interface SustainDerivation extends DerivationEnvelope {
  aspect: 'sustain'
  eventWindow: EventWindow
  assumedDurationHours: number
  spanStartHour: number
  /** Exclusive, like every other span end in this system. */
  spanEndHour: number
  /** Every hour of the span, individually (display rule 11). */
  hours: { hour: number; kw: number; binding: boolean }[]
  nominationKw: number
  bindingHour: number
  overridden: boolean
  flags: EstimateFlag[]
}

export interface ConfigDerivation extends DerivationEnvelope {
  aspect: 'config'
  configVersionId: string
  label: string
  effectiveFrom: string
  season: { start: string; end: string }
  eventWindow: EventWindow
  events: { assumedCount: number; assumedDurationHours: number }
  rates: { energyPerKwh: number; demandPerKwMonth: number; demandMonths: number }
  baseline: {
    method: string
    dayOfAdjustment: { enabled: boolean; capFraction: number; windowHoursBefore: number }
  }
}

/**
 * Aspect to payload, so `api.derivation(id, 'sustain')` is typed as the sustain
 * payload rather than as a union a caller has to narrow by re-reading the
 * argument it just passed.
 */
export interface DerivationByAspect {
  baseline: BaselineDerivation
  factors: FactorsDerivation
  sustain: SustainDerivation
  config: ConfigDerivation
}

/**
 * The engine's seven end uses, as a closed set.
 *
 * Written out rather than left as `string` because the scenario route validates
 * `excludedEndUses` against `END_USES` itself and answers 400 to anything else
 * — deliberately, so that a rep who asks "what if we excluded refrigeration"
 * and mistypes the end use is told, rather than shown the number they were
 * already looking at. A `string[]` here would move that discovery from the type
 * checker to the network.
 */
export type EndUse =
  | 'cooling' | 'ventilation' | 'lighting' | 'refrigeration' | 'process' | 'plug' | 'other'

/** The five overrides a what-if may set. Mirrors the engine's `Overrides`. */
export interface ScenarioOverrides {
  eventDurationHours?: number
  eventCount?: number
  excludedEndUses?: EndUse[]
  nominationKw?: number
  /** `null` is "what if nobody had characterized it", which is not the same as absent. */
  buildingTypeOverride?: string | null
}

export interface ScenarioRequest {
  name: string
  /** Required by the route — send `{}` for a scenario that changes nothing. */
  overrides: ScenarioOverrides
}

/**
 * What a scenario is pinned to and how it was produced. Transcribed from
 * `ScenarioEnvelope` in `packages/server/src/http/routes/scenarios.ts`.
 *
 * Nothing here is persisted: the route computes and answers 200, never 201, and
 * writes no `estimates` row. `recordEstimateId` and `runId` name the *record*
 * this scenario is the comparison for, not anything the scenario is stored as.
 */
export interface ScenarioEnvelope {
  /** The rep's own label for the question, echoed back exactly as sent. */
  name: string
  /** The overrides as validated and applied — read back, not the request's copy. */
  overrides: ScenarioOverrides
  asOfDate: string
  runId: string
  recordEstimateId: string
  configVersionId: string
  characterizationVersionId: string | null
  inputDigest: string
  /**
   * **A statement about `estimate.confidence`, and the reason this panel does
   * not draw one.**
   *
   * `estimates.confidence` on the record is post-cohort: `assignCohorts` runs
   * after every account in a run is scored and steps outliers one rung down.
   * All 26 Medium accounts on the seeded book are cohort step-downs. A scenario
   * belongs to no run and has no cohort, so its label is the engine's own
   * pre-cohort one — measured, `acct-fill-0035` is `medium` as a record and
   * `high` as a scenario **with no overrides at all**.
   *
   * Two labels side by side with no explanation would show every Medium account
   * on the book improving the instant a rep opened a what-if. See
   * `components/WhatIf.tsx`.
   */
  confidenceBasis: 'pre_cohort'
}

/**
 * The figure-bearing half of an estimate — the part a record and a scenario
 * both carry, in the same field names.
 *
 * It exists so the drill-down can render either without a branch at every
 * figure. What a scenario does **not** have is what a record's `id`, `runId`
 * and `createdAt` name (a stored row) and its `eventWindow` (the run's stamped
 * configuration); those stay on `AccountEstimate`.
 */
export interface EstimateFigures {
  confidence: ConfidenceLabel
  method: string
  curtailableKwRaw: number
  /** `raw × confidenceWeights[label]`, computed by the server. Never here. */
  curtailableKwWeighted: number
  baselinePeakKw: number | null
  result: EstimateResultView
}

export type ScenarioEstimate = EstimateFigures

export interface ScenarioPayload {
  accountId: string
  scenario: ScenarioEnvelope
  estimate: ScenarioEstimate
}

export interface SavedList {
  id: string
  name: string
  ownerId: string
  assignedToId: string | null
  accountIds: string[]
  createdAt: string
}

/**
 * A rep a saved list may be handed to, from `GET /api/users/assignable`.
 *
 * **Two fields, and that is the whole payload.** No email and no role: the
 * role is `rep` by construction (the route serves nobody else) and the email
 * is a contactable identifier a picker has no use for. Transcribed from
 * `AssignableUser` in `packages/server/src/auth/users.ts`.
 */
export interface AssignableUser {
  id: string
  displayName: string
}

/**
 * The event window the estimate was computed under, from the run's **stamped**
 * configuration version.
 *
 * On the wire because the alternative was a `17`/`22` written into this
 * package, and program configuration in the browser is configuration nobody can
 * version, audit or change without a release — the same mistake as the mock's
 * client-side confidence weights, one field over. `endHour` is exclusive.
 */
export interface EventWindow {
  startHour: number
  endHour: number
}

/** One hour of the profile. The engine's `HourValue`. */
export interface HourValue {
  hour: number
  kw: number
}

/**
 * An advisory signal on an estimate. **Internal, always** — display rule 7
 * keeps flag text out of every customer-facing surface, and the server's own
 * projection strips flags from the presentation payload and the one-pager, so
 * nothing typed here can reach either.
 */
export interface EstimateFlag {
  code: string
  severity: 'info' | 'warn'
  message: string
  internalOnly: boolean
}

export interface Nomination {
  kw: number
  bindingHour: number
  spanStartHour: number
  /** Exclusive: `curtailment.ts` reports the last hour examined plus one. */
  spanEndHour: number
  /** True when `kw` was entered by hand rather than read off the profile. */
  overridden: boolean
  flags: EstimateFlag[]
}

export interface Valuation {
  seasonTotal: number
  seasonEnergy: number
  seasonDemand: number
  perEventTotal: number
  perEventEnergyKwh: number
  downside: number | null
}

/** `null` on the fallback path, where no decomposition was performed. */
export interface Decomposition {
  baseKw: number
  coolingKwPerDegreeHour: number
  scheduleLiftKw: number
  scheduleOnHours: number[]
  fitR2: number
  reconciliationErrorKw: number
}

export interface ConfidenceResult {
  label: ConfidenceLabel
  reasons: string[]
  characterizationContributed: boolean
}

/**
 * The part of the engine's `EstimateResult` the drill-down draws.
 *
 * Deliberately partial, and deliberately hand-written. This package has no
 * reference to the engine or the server project — on purpose, so that nothing
 * in the SPA can import a module that computes — so the alternative to
 * transcribing what is rendered is several hundred lines of type nothing checks
 * against the wire. `baseline`, `components`, `endUseBreakdown` and `cohort`
 * stay in the untyped remainder because no screen reads them yet; each screen
 * narrows the part it renders as it lands.
 */
export interface EstimateResultView {
  asOfDate: string
  method: 'decomposition' | 'fallback'
  profile: HourValue[]
  /**
   * The account's actual load for the chart's context hours, outside
   * `eventWindow` — a plain meter average, never a curtailment claim. See the
   * engine's `EstimateResult.contextLoad`. Merged with `profile` only at the
   * point a screen builds `<ProfileChart>`'s props, never earlier: nothing
   * else on this page reads it, and `profile` alone is still what the
   * nomination and valuation figures came from.
   */
  contextLoad: HourValue[]
  nomination: Nomination
  value: Valuation
  decomposition: Decomposition | null
  confidence: ConfidenceResult
  narrative: string
  flags: EstimateFlag[]
}

export interface AccountEstimate extends EstimateFigures {
  id: string
  /**
   * The run that produced this estimate, or `null` for one that belongs to no
   * run. `null` is not a missing value here: it is how an estimate written by
   * an analyst's approval is told apart from one written by the batch runner
   * (see {@link AccountDetail.rescoredSinceRun}).
   */
  runId: string | null
  createdAt: string
  eventWindow: EventWindow
  /**
   * The configuration version and approved characterization revision this
   * estimate was stamped with (R-22), from `DrilldownEstimate` on the server.
   *
   * Rendered only where two estimates appear at once, which is the only place
   * an identifier is worth a reader's attention: the drill-down draws the
   * ranked figure beside a re-scored one, and these two fields are what let it
   * say which input differs between them instead of leaving a reader to guess.
   * `characterizationVersionId` is `null` on an estimate scored for an account
   * that had no approved revision at the time.
   */
  configVersionId: string
  characterizationVersionId: string | null
}

/** The account attributes the drill-down header and its right rail read. */
export interface DetailAccount {
  id: string
  accountNumber: string
  name: string
  serviceAddress: string
  naics: string
  naicsLabel: string
  rateClass: string
  premiseId: string
  enrollmentStatus: 'enrolled' | 'not_enrolled' | 'declined'
  /** The contractual figure of record, which is not the current estimate. */
  nominatedKw: number | null
  eligibilityStatus: 'in_scope' | 'out_of_scope'
  eligibilityReason: string
  derStatus: 'known_pv' | 'confirmed_none' | 'unknown'
  pvNameplateKw: number | null
}

/** Display rule 6: who entered a human-recorded fact, when, from what source. */
export interface CharacterizationProvenance {
  enteredBy: string
  enteredAt: string
  researchSource: string
}

/**
 * The three characterization states of display rule 3, as the server resolves
 * them — at the revision the estimate was pinned to.
 *
 * `uncharacterized` and `undetermined` arrive as distinct values rather than as
 * two readings of a nullable building type, which is what keeps them from
 * collapsing into each other on the way to a screen.
 */
export interface AccountCharacterization {
  state: 'recorded' | 'undetermined' | 'uncharacterized'
  buildingType: string | null
  version: number | null
  provenance: CharacterizationProvenance | null
}

/**
 * The pinned or most recent note (spec §7.2), read-only.
 *
 * There is no write side and this type must not grow one, which is why the feed
 * below is a **separate** type rather than five more fields here. This one is
 * the *summary*: one note, chosen by the server, never a redacted one
 * (`http/routes/accounts.ts:588` — "not optional and not a nicety", because the
 * summary shows the pinned or most recent note and a withheld body must never
 * be that). Growing `redactedAt` onto it would describe a row this payload
 * cannot carry.
 */
export interface AccountNote {
  id: string
  body: string
  authorName: string
  createdAt: string
  pinned: boolean
  tag: string | null
}

/** The drill-down envelope, as served with no query parameters at all. */
export interface AccountDetail {
  account: DetailAccount
  estimate: AccountEstimate | null
  characterization: AccountCharacterization
  note: AccountNote | null
  /**
   * The estimate an analyst's approval re-scored this account to **since the
   * run the targeting list ranks from** — `null` on every account nobody has
   * reviewed since, which is nearly all of them.
   *
   * Transcribed from `DrilldownPayload.rescoredSinceRun`, which states the full
   * argument. The short version, because it decides how this is drawn: an
   * approval re-scores exactly one account and writes an estimate that belongs
   * to no run (`runId: null`), and the targeting list deliberately stays on the
   * ranked run's numbers, because a list in which some rows carry an approval's
   * figure and the rest carry the run's is not a ranking of anything.
   *
   * So `estimate` above is the ranked figure — what the list sorted on, what
   * the one-pager prints, what the header and the KPI band draw — and this is a
   * **second** figure beside it, never in place of it. The two must not be
   * confusable on screen; `AccountDetail.tsx`'s `ProgramStatusCard` is where
   * they sit together and is where that is enforced.
   *
   * It never reaches a customer: `?mode=presentation` is a different payload
   * built server-side, and the projection does not copy this field.
   */
  rescoredSinceRun: AccountEstimate | null
  /**
   * The characterization the **re-scored** estimate was computed against, or
   * `null` when there is no re-score.
   *
   * `characterization` above is pinned to the *ranked* estimate's revision
   * (Plan 3's C-1) and stays that way: the card beside the ranked figure has to
   * describe what that figure was computed from. The consequence, measured, is
   * that an `undetermined` approved this morning reads
   * `state: 'uncharacterized'` there until the next run — display rule 3's
   * middle state (*somebody looked and could not tell*) collapsed into its
   * first (*nobody has looked*), which is the distinction that decides whether
   * anyone is sent to look again.
   *
   * This is that state, beside the figure it belongs to. Two cards, each
   * describing its own estimate, neither rewriting the other.
   */
  rescoredCharacterization: AccountCharacterization | null
}

/**
 * The note tag vocabulary (spec §7.7, design brief §4.6) — the six values
 * `config.noteTags` carries, written out here because **no route serves them.**
 *
 * That is the honest statement of the situation rather than a preference. The
 * vocabulary is program configuration, this application has no configuration
 * route, and a composer has to draw *something* on its tag control. The copy is
 * safe in a way the mock's client-side confidence weights were not: nothing
 * here is arithmetic and nothing here is authoritative. `createNote` resolves
 * `config.noteTags` at the note's own `createdAt` and answers **400** to a tag
 * that is not in it, so a stale copy in this file fails loudly at the moment a
 * rep presses Save — it cannot write a value into the column that R-29's
 * set-retrieval would then never find again.
 *
 * The *labels* are not written out. `asWords` derives `Curtailment constraint`
 * from `curtailment_constraint`, for the reason it already derives building
 * type labels: a second hand-written table goes stale the first time somebody
 * edits the configuration and is right about nothing in the meantime.
 */
export const NOTE_TAGS = [
  'curtailment_constraint', 'site_condition', 'contact', 'scheduling', 'competitive', 'other',
] as const

export type NoteTag = (typeof NOTE_TAGS)[number]

/**
 * One row of an account's note feed — **the record, redactions included.**
 *
 * Transcribed from `NoteRow` in `packages/server/src/notes/store.ts`, and it is
 * a different type from `AccountNote` above because it answers a different
 * question. The summary asks "what is the one note to show beside the figures";
 * the feed *is* the record, and a record with silent holes is not one. So a
 * redacted note arrives here rather than being dropped — with `body` **absent**
 * and `redactedAt`, `redactedBy` and `redactionReason` set. That is the second
 * of the five redaction rules `notes/search.ts` tabulates; say which surface you
 * are in before changing either.
 *
 * `body?: string` rather than `body: string | undefined` because that is what
 * comes off the wire: the store serves `body: undefined` and `JSON.stringify`
 * drops the key entirely. Under `exactOptionalPropertyTypes` the second spelling
 * would promise a key that is not there.
 */
export interface FeedNote {
  id: string
  accountId: string
  authorId: string
  authorName: string
  /** Absent — not empty, not null — when the note has been redacted. */
  body?: string
  tag: string | null
  pinned: boolean
  pinnedBy: string | null
  createdAt: string
  updatedAt: string
  /** The most recent edit, or `null` if the note has never been edited. */
  editedAt: string | null
  /** How many superseded versions `note_revisions` holds for this note. */
  revisionCount: number
  redactedAt: string | null
  redactedBy: string | null
  redactionReason: string | null
}

/**
 * One superseded version of a note, from `GET /api/notes/:id/history`.
 *
 * `body` is the text as it stood *before* the edit that produced the next
 * version — which is the whole point of the surface: "Entries are permanent — a
 * correction is a new entry" is a promise the screen makes, and a UI that
 * silently replaced the text under that caption would be lying to its user.
 */
export interface NoteRevision {
  noteId: string
  version: number
  body: string
  tag: string | null
  editedBy: string
  editedAt: string
}

/**
 * One hit from `GET /api/notes/search` (R-29) — transcribed from
 * `NoteSearchResult` in `packages/server/src/notes/search.ts`.
 *
 * **`body` is `string`, not `string | undefined`.** A redacted note is not a
 * result at all — search *excludes* the row where the account feed serves it
 * with the body withheld — so there is no such value as a hit with nothing in
 * it. That is the fourth of the five redaction rules `notes/search.ts`
 * tabulates, and it is why this type is not `FeedNote`: the two surfaces
 * disagree about redaction on purpose, and one type covering both would have to
 * make `body` optional and lose the guarantee.
 *
 * The account travels on the hit rather than being fetched per row, because the
 * result list links to it and a request per hit is a request per hit.
 */
export interface NoteSearchHit {
  id: string
  accountId: string
  accountName: string
  accountNumber: string
  authorId: string
  authorName: string
  body: string
  tag: string | null
  pinned: boolean
  createdAt: string
  updatedAt: string
}

/**
 * The four dimensions `GET /api/notes/search` recognises (R-29), and nothing
 * else — the route answers 400 to any other key, so this type is that
 * guarantee's client-side half, the same as `AccountFilters`.
 *
 * `from` and `to` are `YYYY-MM-DD` or a full ISO-8601 instant; the server
 * expands a bare day at the upper end to the end of that day, so a search "to
 * 25 August" includes a note written at nine that morning.
 */
export type NoteSearchFilters = {
  text?: string
  tag?: NoteTag
  authorId?: string
  from?: string
  to?: string
}

/**
 * A curtailment strategy as the library states it — transcribed from
 * `Strategy` in `packages/engine/src/strategy-library.ts`, for the same reason
 * every other payload type in this file is transcribed rather than imported:
 * this package has no dependency on the engine or the server, and is bundled
 * into a browser.
 *
 * `framingStatement` is the **strategy's own** sentence, written by whoever
 * committed the library. It is not the surface-level framing R-33 mandates;
 * that one is `STRATEGY_FRAMING` in `components/StrategyList.tsx` and travels
 * with the *list*, once, whatever the list contains.
 */
export interface Strategy {
  id: string
  name: string
  framingStatement: string
  endUses: EndUse[]
  yieldFraction: number
  impact: 'low' | 'medium' | 'high'
  feasibility: 'low' | 'medium' | 'high'
  /** `hours` is half-open local hours: `startHour` inclusive, `endHour` exclusive. */
  timing:
    | { kind: 'pre_event' }
    | { kind: 'in_window' }
    | { kind: 'hours'; startHour: number; endHour: number }
  prerequisites: string[]
  applicableNaicsPrefixes: string[]
  applicableBuildingTypes: string[]
}

/**
 * The whole library document — `StrategyLibrary` in
 * `packages/engine/src/strategy-library.ts`.
 *
 * `version` is the **document's own** number, written by whoever commits it. It
 * is not a schema version and there is no migration chain for this document:
 * `strategy_library_versions.document` is opaque append-only JSON, which is why
 * `Strategy.timing` was carried from the first version rather than added later.
 */
export interface StrategyLibrary {
  version: number
  strategies: Strategy[]
}

/**
 * `GET /api/strategies` — the library in force today, and the id it was
 * committed under.
 *
 * `id: null` with an empty `strategies` is a **designed state**, not a missing
 * resource: the route answers 200, because a program that has committed no
 * library yet is a program, and `StrategyList.tsx` prints different words for
 * that than for "no row matched this account". The console's strategy tab makes
 * the same distinction — an empty library is something to write, not an error.
 *
 * `requireAuth`, not `requireRole`: a rep is the person who reads a framing
 * statement out to a customer. Only the **commit** is gated.
 */
export interface StrategyLibraryInForce {
  id: string | null
  library: StrategyLibrary
}

/**
 * `POST /api/strategies` — the commit envelope, **not** the bare library.
 *
 * `strategy_library_versions` is effective-dated like `config_versions`, so a
 * route taking the document alone would have to invent the date and the only
 * defensible invention is "today" — which makes scheduling next season's
 * library impossible through the only write path there is. `committedBy` and
 * `committedAt` are deliberately absent: the server takes them from the session
 * and its own clock.
 */
export interface StrategyLibraryCommit {
  document: StrategyLibrary
  effectiveFrom: string
}

/** What a library commit answers with. **201**, and there is no `label` column. */
export interface StrategyLibraryCommitted {
  id: string
}

/**
 * One ranked strategy — `RankedStrategy` from `packages/engine/src/strategies.ts`.
 *
 * `indicative` is the literal `true` on the wire and in this type for the
 * reason the engine gives: there is no such value as a non-indicative ranked
 * strategy, so no branch in this package can produce one and every reader sees
 * the word.
 *
 * `statedPrerequisites` is every prerequisite the strategy **declares**.
 * Nothing in this system knows which of them the account meets, so a UI
 * rendering "Unmet: building management system" would be asserting a fact no
 * input supports. The field is named for what it holds; render it as a
 * requirement.
 *
 * `notApplicable` is the fallback path — the strategy is shown, with the
 * reason, and is not quantified. 43% of the seeded book lands here and it is
 * neither an empty list nor an error.
 */
export interface RankedStrategy {
  strategy: Strategy
  indicativeKw: number
  indicative: true
  score: number
  statedPrerequisites: string[]
  notApplicable: { reason: string } | null
}

/**
 * The R-33 aggregate — `StrategyAggregate` from the engine.
 *
 * `indicativeTotalKw` is overlap-adjusted and capped at the metered estimate.
 * **It is never a plain sum**, and no screen in this package may compute one:
 * adding `indicativeKw` across rows is the arithmetic this figure exists to
 * replace.
 */
export interface StrategyAggregate {
  indicativeTotalKw: number
  indicative: true
  byEndUse: { endUse: EndUse; claimedKw: number; availableKw: number; creditedKw: number }[]
  capped: boolean
}

/**
 * `GET /api/accounts/:id/strategies` — `AccountRanking` from
 * `packages/server/src/strategies/rank.ts`.
 *
 * `strategyLibraryVersionId` is `null` when no library is in force, and that is
 * a designed state rather than an error: `ranked` is `[]` and the route answers
 * 200.
 *
 * **`ranked: []` is ambiguous on its own and this field is what disambiguates
 * it.** `null` here means nothing could be ranked for *any* account; a version
 * id with an empty `ranked` means a library was in force and none of its rows
 * matched *this* account's industry. `StrategyList.tsx` reads this field for
 * exactly that reason and prints different words for the two — it used to print
 * the industry sentence for both, which was a false statement to a user on
 * every one of the 313 accounts whenever the library table was empty.
 */
export interface AccountRanking {
  strategyLibraryVersionId: string | null
  ranked: RankedStrategy[]
  aggregate: StrategyAggregate
}

/**
 * What a rejection answers with (R-33): the end-use exclusion it **offers**.
 *
 * The rejection itself moves no number — the route recomputes nothing and
 * persists no estimate. `suggestedOverride.excludedEndUses` is every end use
 * the rejected strategy addresses, in the engine's `END_USES` order, and it may
 * name an end use outside `WhatIf.tsx`'s three-checkbox `EXCLUDABLE` shortlist:
 * `POST /api/accounts/:id/scenarios` validates against all seven and
 * `computeProfile` honours all seven. See `WhatIf.tsx` on what that surface
 * does with the other four.
 */
export interface StrategyRejection {
  suggestedOverride: ScenarioOverrides
}

/**
 * The same account scored for a different day (`?asOf=`, Task 8), **persisted
 * nowhere** — transcribed from `RecomputedEstimate` in
 * `packages/server/src/http/routes/accounts.ts`.
 *
 * It extends `EstimateFigures` because that is exactly what it is: the same
 * four figures and the same result document, for another date. What it adds is
 * the two statements a rep has to read *with* the figure — `ofRecord: false`
 * and the day it was computed for — and they sit here, on the estimate, rather
 * than only on the envelope, for the reason display rule 1 puts the confidence
 * label beside every kW figure: a rep quoting a number reads the number.
 *
 * What it does **not** carry is `id`, `runId`, `createdAt` and `eventWindow`.
 * The first three are absent because nothing was stored and a fabricated id
 * would claim otherwise. `eventWindow` is absent because the recompute runs
 * under the *record's* stamped configuration version, so the window it was
 * measured against **is** the record's — the chart reads it from there, and a
 * copy on this payload would be a second reading that could disagree. The same
 * argument `ScenarioEstimate` makes, for the same reason.
 */
export interface RecomputedEstimate extends EstimateFigures {
  ofRecord: false
  /** The day this figure was computed for, echoed back exactly as requested. */
  asOfDate: string
}

/**
 * What a recompute is pinned to. Transcribed from `RecomputeEnvelope`
 * (`routes/accounts.ts`).
 *
 * The configuration version and the characterization revision are both the
 * record's, so the only thing that differs between the two figures is the day.
 * `confidenceBasis` is the same trap `ScenarioEnvelope` carries and has the
 * same answer on screen: the page keeps naming the **record's** label, because
 * a recompute belongs to no run, has no cohort, and its own label is the
 * engine's pre-cohort one.
 */
export interface RecomputeEnvelope {
  runId: string
  recordEstimateId: string
  /** The record's own as-of date — the other half of the contrast. */
  recordAsOfDate: string
  configVersionId: string
  characterizationVersionId: string | null
  inputDigest: string
  confidenceBasis: 'pre_cohort'
}

export interface RecomputedPayload {
  account: DetailAccount
  estimate: RecomputedEstimate
  recompute: RecomputeEnvelope
}

/**
 * The customer-facing payload, transcribed from the server's own
 * `http/presentation.ts` — `PresentationPayload` there, field for field.
 *
 * **It is a separate type because it is a separate payload.** R-28's mechanism
 * is that the internal fields never reach the browser at all: a screen that
 * receives notes and flags and declines to draw them looks identical and
 * satisfies nothing. A type that describes the customer payload as "the
 * drill-down, some of whose fields happen to be missing" invites exactly the
 * implementation the requirement forbids — fetch everything, render some of it
 * — and would type-check while doing it.
 *
 * Written out rather than derived from `AccountDetail` with `Omit`, for the
 * same reason the server's projection is: a subtractive type grows a new field
 * on the day the drill-down grows one, which is the day it reaches a customer.
 *
 * There is no `flags`, no `narrative`, no `confidence.reasons`, no
 * `decomposition`, no `cohort`, no `characterization` and no `note` here, and
 * their absence is not an omission to be filled in later. `curtailableKwRaw`
 * is present and `curtailableKwWeighted` is not: the weighted figure is an
 * internal ranking multiplier (R-16), and a second, smaller kW number on a
 * customer's screen with no stated derivation is worse than no number at all.
 */
export interface PresentationAccount {
  id: string
  accountNumber: string
  name: string
  serviceAddress: string
}

/** `Nomination`, minus its flags — which the projection does not send. */
export interface PresentationNomination {
  kw: number
  bindingHour: number
  spanStartHour: number
  /** Exclusive, as `curtailment.ts` reports it. */
  spanEndHour: number
  overridden: boolean
}

export interface PresentationEstimate {
  /** Display rule 1 admits no bare kW figure, so the label crosses even here. */
  confidence: ConfidenceLabel
  curtailableKwRaw: number
  baselinePeakKw: number | null
  /** The stamped window the chart's title and its band are derived from. */
  eventWindow: EventWindow
  result: {
    profile: HourValue[]
    nomination: PresentationNomination
    value: Valuation
  }
}

export interface PresentationPayload {
  account: PresentationAccount
  estimate: PresentationEstimate | null
}

/**
 * The configuration in force today, as `GET /api/config/current` answers it —
 * **a projection, and not the document.**
 *
 * Seven numbers and two dates, and the bound on them is the one-pager: every
 * one is already printed to customers in its "Assumptions this rests on" block,
 * server-side, so this route discloses nothing that is not on a document the
 * customer keeps. It is a bound on *fields*, not on sections — whole `rates`
 * would carry the shortfall penalty schedule and the nomination floor and
 * ceiling, and whole `events` the notification lead time, none of which appears
 * on any customer's copy; see `routes/config.ts`'s `CurrentConfigView`.
 *
 * The shed factor table, the end-use mix, the confidence thresholds, the cohort
 * percentiles and the eligibility rules are the program's internal model and
 * the configuration console's editable surface; a browser that received them
 * would make them a de-facto client API. A program manager who needs the whole
 * document has `GET /api/config/at`, which is role-gated.
 *
 * This is the route two shipped components documented wanting and worked
 * around — `AccountDetail.tsx`'s as-of bounds and `Presentation.tsx`'s
 * assumptions line — and both of those docblocks now describe using it rather
 * than the absence of it.
 */
export interface CurrentConfig {
  /** The version id, so a caller can say "version 4" rather than "the config". */
  id: string
  effectiveFrom: string
  season: { start: string; end: string }
  eventWindow: { startHour: number; endHour: number }
  events: { assumedCount: number; assumedDurationHours: number }
  rates: { energyPerKwh: number; demandPerKwMonth: number; demandMonths: number }
}

/** One row of the curtailment factor table. `null` in either key is a wildcard. */
export interface ConfigFactorRow {
  naics: string | null
  /**
   * `null` is the **industry-only row**, and it is not a fallback to tidy away:
   * it is how an uncharacterized account is scored at all (display rule 4,
   * R-23). The console says so under the table rather than leaving a reader to
   * guess that a blank cell is missing data.
   */
  buildingType: string | null
  endUse: string
  shedFraction: number
  shiftFraction: number
}

export interface ConfigOccupancyPeriod {
  start: string
  end: string
  /** 1.0 = normal operation, 0.1 = effectively unoccupied (R-23). */
  occupancyFactor: number
}

export interface ConfigBuildingType {
  id: string
  label: string
  /** `null` means "occupied normally all season" — not "no calendar was entered". */
  occupancyCalendar: ConfigOccupancyPeriod[] | null
}

/**
 * The whole configuration document, as `GET /api/config/at` and the draft
 * routes carry it — **program-manager only**, and deliberately not what
 * {@link CurrentConfig} is.
 *
 * ## This is a partial transcription, and that is safe here for one reason
 *
 * `packages/server/src/config/defaults.ts` carries sixteen top-level sections;
 * **seven** are named below because seven are what the console edits (brief
 * §4.7's six surfaces). The rest — `weather`, `decomposition`, `sufficiency`,
 * `confidence`, `cohort`, `eligibility`, `ranking`, `endUseMix`, `noteTags`,
 * `noteRetentionDays`, `timezone` — arrive on the wire, are held in this object
 * at runtime, and travel back out untouched.
 *
 * `strategyWeights` and `negligibleShareThreshold` moved out of that list in
 * Plan 6 Task 7a, which built the strategy-library surface. They are named
 * because they are now edited, and naming them is what makes the two controls
 * type-checked rather than a pair of casts.
 *
 * They survive because **every edit in `ConfigConsole` is a spread of the
 * document it was handed**, never a reconstruction from named fields. A field
 * this interface does not mention is therefore invisible to the compiler and
 * intact on the wire. The one thing that would break it is building a commit
 * body out of literals; `commitConfig` on the server revalidates, so that
 * mistake is a 400 naming the missing section rather than a version committed
 * with half a document — but it is worth not making.
 *
 * `schemaVersion` is `number` rather than a literal on purpose: the browser
 * does not own the schema, migrations run on read in `config/store.ts`, and a
 * literal here would turn a server-side bump into a compile error in a package
 * that has no opinion about it.
 */
export interface ConfigDocument {
  schemaVersion: number
  season: { start: string; end: string }
  eventWindow: { startHour: number; endHour: number }
  events: {
    assumedCount: number
    assumedDurationHours: number
    minLengthHours: number
    maxLengthHours: number
    notificationLeadMinutes: number
  }
  rates: {
    energyPerKwh: number
    demandPerKwMonth: number
    demandMonths: number
    shortfall: { penaltyPerKw: number; toleranceFraction: number } | null
    nominationFloorKw: number
    nominationCeilingKw: number
  }
  baseline: {
    method: '10in10' | '5in10' | 'high4of5'
    lookbackDays: number
    excludeWeekends: boolean
    excludePriorEventDays: boolean
    dayOfAdjustment: { enabled: boolean; capFraction: number; windowHoursBefore: number }
  }
  factors: ConfigFactorRow[]
  buildingTypes: ConfigBuildingType[]
  /**
   * How the ranked strategy list is ordered (R-32) — **unbounded relative
   * weights, not shares**. `validateConfig` gives all three a bare
   * `atLeast(…, 0)` and no upper bound, deliberately, and this type says
   * nothing stronger than the server does.
   *
   * They move no kW. `computeBlastRadius` lists `strategyWeights` in
   * `CONFIG_PATHS_NOT_EVALUATED` for that reason, so a preview of a change here
   * is a true zero over an unmeasured question; the console says so where the
   * control is.
   */
  strategyWeights: { yield: number; impact: number; feasibility: number }
  /**
   * The share of an account's curtailable load below which an end use is too
   * small for a strategy addressing it to be quantified against (R-32) — a
   * share in [0, 1], `range(…, 0, 1)` on the server.
   *
   * A real config field since schema 7 (Plan 6 Task 0) precisely so the
   * strategy-library surface has a control for it rather than a sentence
   * explaining that the sixth knob is a code change.
   */
  negligibleShareThreshold: number
}

/** One committed, immutable configuration version (R-12, R-22). */
export interface ConfigVersion {
  id: string
  label: string
  effectiveFrom: string
  document: ConfigDocument
}

/**
 * One field the draft moves, against the version in force.
 *
 * `from` and `to` are `unknown` on the server (`config/draft.ts`) because a
 * diff walks a document generically. `JsonValue` is the same thing narrowed to
 * what actually crosses a JSON wire, which is what lets the console print a
 * value without a cast.
 */
export interface ConfigDiff {
  /** Dotted, with array indices: `rates.energyPerKwh`, `factors[3].shedFraction`. */
  path: string
  from: JsonValue
  to: JsonValue
}

/** This program manager's saved draft, why it would be refused, and what it moves. */
export interface ConfigDraftView {
  /** `null` when nothing has been drafted. Drafts are per user. */
  document: ConfigDocument | null
  diff: ConfigDiff[]
  /** `validateConfig`'s own list — the same reasons `commitConfig` would refuse it. */
  errors: string[]
}

/** The account that moved most, by |Δ kW| and then by |Δ season value|. */
export interface BlastRadiusChange {
  accountId: string
  name: string
  fromKw: number
  toKw: number
  fromSeasonValue: number
  toSeasonValue: number
}

export interface ConfidenceChange {
  accountId: string
  from: ConfidenceLabel
  to: ConfidenceLabel
}

/**
 * What would change if this candidate configuration were committed (R-13),
 * transcribed from `packages/server/src/config/blast-radius.ts`.
 *
 * Two fields decide whether this panel is a warning or decoration, and both are
 * absent from the wireframe that drew the rest of it:
 *
 * - `sampled` — non-null means the figures describe a sample, and every count
 *   here is a count over that sample rather than over the book.
 * - `blindTo` — dotted paths the draft changes that the preview cannot
 *   evaluate. Non-empty means a headline of zero is not evidence that nothing
 *   moves, which is exactly how a screen that looks like verification launders
 *   an edit nobody checked.
 *
 * `reach` is the third, and it answers the other kind of zero — the one
 * `blindTo` structurally cannot see, because the path *was* evaluated and the
 * book simply cannot feel it. See `BuildingTypeConfigReach` on the server for
 * the measurement: a `k12_school` occupancy edit moves 0 of 313 with
 * `blindTo: []` and a green banner.
 */
export interface BlastRadius {
  /** How many accounts were actually scored. The denominator for everything below. */
  accountsConsidered: number
  /** Accounts where anything a user sees moved: kW, season value, confidence label, or flags. */
  accountsMoved: number
  movedBy: { nominationKw: number; seasonValue: number; confidence: number; flags: number }
  direction: { down: number; up: number; unchanged: number }
  distribution: { bucket: string; count: number }[]
  kwPercentChange: { min: number; median: number; max: number; basis: number } | null
  largestChange: BlastRadiusChange | null
  confidenceChanges: ConfidenceChange[]
  /**
   * Contractual shortfall exposure, split into what this commit *changes* and
   * what the book carries either way.
   *
   * It was one number — enrolled accounts nominated above the estimate the
   * candidate produces — and the panel read that as a consequence of the draft.
   * It is not: measured exhaustively on the seeded book, an identical document
   * that moved nothing reported 21, and so did an edit that moved all 313. The
   * server now reports both sides; see `config/blast-radius.ts` for the
   * arithmetic and the measurement.
   */
  shortfallExposure: {
    /** Already above under the document in force. True with or without this commit. */
    standing: number
    /** Above under the candidate and not under the document in force — created by this commit. */
    newlyExposed: number
    /** Above under the document in force and not under the candidate — removed by this commit. */
    relieved: number
  }
  blindTo: string[]
  /**
   * One entry per building type whose configuration the draft edits, with what
   * the scored population has of it. Empty for every draft that edits no
   * building type, which is most of them.
   */
  reach: BuildingTypeConfigReach[]
  sampled: { of: number; sampled: number } | null
}

/** Transcribed from `config/blast-radius.ts`'s interface of the same name. */
export interface BuildingTypeConfigReach {
  buildingType: string
  paths: string[]
  /** Scored in-scope accounts resolving to this type — `undetermined` resolves to none. */
  accountsResolving: number
  /** Of those, how many scored by `fallback`, which applies no occupancy calendar. */
  scoredByFallback: number
  /** Whether every changed path under this type is inside its occupancy calendar. */
  occupancyOnly: boolean
}

export interface BlastRadiusPreview {
  diff: ConfigDiff[]
  radius: BlastRadius
}

export interface ConfigCommit {
  document: ConfigDocument
  label: string
  effectiveFrom: string
  notes?: string
}

export interface ConfigCommitted {
  id: string
  label: string
  effectiveFrom: string
}

/**
 * One proposal waiting on an analyst — `packages/server/src/characterization/store.ts`'s
 * `QueueRow`, field for field.
 *
 * **There is deliberately no `state`.** Every row the queue route sends is
 * `proposed` by construction, and a field that is a constant invites a screen
 * to filter on it as though it might be something else. The store says the
 * same thing about its own type.
 *
 * `buildingType` is never `null`: a row that exists always names one of the
 * configured types, and `'undetermined'` — *somebody looked and could not
 * tell* — is a member of that vocabulary rather than a way of leaving it out.
 */
export interface CharacterizationQueueRow {
  id: string
  accountId: string
  accountNumber: string
  accountName: string
  version: number
  buildingType: string
  researchSource: string
  enteredBy: string
  enteredByName: string
  enteredAt: string
}

/** One side of the impact comparison — the figures the review panel's cells show. */
export interface CharacterizationEstimatePreview {
  curtailableKwRaw: number
  seasonValueTotal: number
  /** **Pre-cohort on both sides**, so the difference is the characterization and nothing else. */
  confidence: ConfidenceLabel
  method: 'decomposition' | 'fallback'
  /** `null` where no characterization applied; the recorded string otherwise. */
  buildingType: string | null
  characterizationVersion: number | null
}

export interface CharacterizationImpactDelta {
  /** `ifApproved - current`. Negative means approval lowers the nomination. */
  kw: number
  seasonValue: number
  confidenceFrom: ConfidenceLabel
  confidenceTo: ConfidenceLabel
  /** R-26's uplift actually fired on the `ifApproved` side. */
  characterizationContributed: boolean
}

/**
 * Whether the proposed building type resolves any configuration of its own.
 *
 * The wireframe's *"No change — industry row still governs"* as a fact about
 * the document rather than an observation of a zero. **The implication runs one
 * way**: no reach means no kW movement, structurally; reach does *not* imply
 * movement — `fallbackProfile` never applies an occupancy calendar, and a
 * calendar that says "full campus" in September moves nothing in September.
 * `characterization/impact.ts` argues both halves.
 */
export interface CharacterizationReach {
  factors: boolean
  endUseMix: boolean
  occupancy: boolean
}

export interface CharacterizationImpact {
  characterizationId: string
  accountId: string
  proposedBuildingType: string
  /**
   * The date both sides were scored as of — the latest completed run's.
   *
   * **On screen, always.** Occupancy calendars are seasonal, so the same
   * approval moves an account 129.1 → 64.8 kW in July and not at all in
   * September. A figure whose date is off the screen is a figure a reviewer
   * cannot check.
   */
  asOfDate: string
  configVersionId: string
  current: CharacterizationEstimatePreview
  ifApproved: CharacterizationEstimatePreview
  delta: CharacterizationImpactDelta
  reach: CharacterizationReach
}

/** The three verbs of `characterization/review.ts`, spelled as the route accepts them. */
export type ReviewDecision = 'approve' | 'amend' | 'reject'

/**
 * A decision, and what it did — `characterization/review.ts`'s `ReviewOutcome`.
 *
 * `rescored` and `estimateId` are separate fields rather than one nullable id
 * because they answer different questions: *which row* and *did this happen at
 * all*. Before the first run there is nothing to re-score against and the
 * decision is still recorded, which is the only way `rescored: false` arises.
 */
export interface ReviewOutcome {
  /** For approve and reject the row under review; **for amend the new revision.** */
  characterizationId: string
  accountId: string
  state: 'proposed' | 'approved' | 'rejected'
  version: number
  rescored: boolean
  estimateId: string | null
  /**
   * The revision the re-score was computed against, which is **not always**
   * `version`: an account can already carry an approved revision newer than the
   * one under review, and approving the older one then changes nothing.
   */
  characterizationVersion: number | null
  ranAgainstRunId: string | null
}

/**
 * What a decision asks for. `reason` is required for `amend` and `reject` and
 * refused-as-empty by the server, not by this client: `review.ts` states the
 * rule once so a second caller cannot get a different one.
 */
export interface ReviewRequest {
  decision: ReviewDecision
  reason?: string
  /** Amend only: the type the analyst is approving instead. */
  buildingType?: string
}

/**
 * Query objects are declared as *type aliases* rather than interfaces
 * (`AccountFilters`, `AccountOptions` above) so that they satisfy
 * `Record<string, unknown>` without an index signature — an index signature
 * would defeat the point of naming the keys, which is that a caller cannot
 * invent one the route will answer 400 to.
 */
type Query = Readonly<Record<string, unknown>>

/**
 * Absent keys are absent, not the string `"undefined"`, and an empty filter
 * object produces no `?` at all. `exactOptionalPropertyTypes` means an explicit
 * `undefined` and an omitted key are different things at the type level; here
 * they are deliberately the same thing on the wire, because "I have no opinion
 * about confidence" is not a value this route accepts.
 */
function buildUrl(path: string, query?: Query): string {
  const url = new URL(path, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value))
  }
  return url.toString()
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

async function send(method: Method, path: string, query?: Query, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'

  const init: RequestInit = { method, headers, credentials: 'same-origin' }
  if (body !== undefined) init.body = JSON.stringify(body)

  try {
    return await fetch(buildUrl(path, query), init)
  } catch (cause) {
    // A rejected `fetch` is a transport failure — DNS, a dropped connection, a
    // server that is not listening. There is no response and therefore nothing
    // the server said, so nothing may be inferred about the request.
    throw new ApiError(NETWORK_UNREACHABLE, 'network', null, null, null, { cause })
  }
}

/**
 * The two refusal shapes this server actually sends.
 *
 * Most routes send `{ error, message }` — one code and one sentence. The two
 * configuration routes that run `validateConfig` send `{ error, errors }`
 * instead: `POST /api/config/commit` and `POST /api/config/draft/blast-radius`
 * both refuse with `{ error: 'invalid_configuration', errors: string[] }` and
 * **no `message` at all** (`http/routes/config.ts`). A reader that knew only
 * the first shape turned a refusal naming five bad fields into `detail: null`,
 * and the console fell through to *"The server refused that request."* — one
 * sentence naming none of them, in front of a program manager whose commit had
 * just been rejected for reasons the server had already written down.
 */
interface ErrorBody {
  error?: unknown
  message?: unknown
  errors?: unknown
}

/** Best-effort read of the server's refusal shape. Never throws. */
async function errorBody(response: Response): Promise<ErrorBody> {
  try {
    const parsed: unknown = await response.json()
    return typeof parsed === 'object' && parsed !== null ? parsed as ErrorBody : {}
  } catch {
    return {}
  }
}

/**
 * `errors` as `string[]`, or `[]` — never a partly-typed array.
 *
 * Every element is checked rather than the array as a whole, because this is
 * arbitrary JSON off a socket and a screen that renders `[object Object]` in a
 * list of reasons is worse than one that renders none. A mixed array yields the
 * strings in it: some named reasons beat no named reasons, and the caller still
 * has `detail` and its own fallback for the empty case.
 */
function reasonsFrom(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

async function failureFor(response: Response): Promise<ApiError> {
  const { error, message, errors } = await errorBody(response)
  const code = typeof error === 'string' ? error : null
  const reasons = reasonsFrom(errors)
  // `message` first when the server sent one: it is the sentence that route
  // chose. `reasons[0]` is the fallback rather than a join, because `detail` is
  // one line on screens that render a single sentence and the whole list is on
  // `reasons` for the screens that render a list.
  const detail = typeof message === 'string' ? message : reasons[0] ?? null

  if (response.status === 401) {
    return new ApiError(SESSION_ENDED, 'session_ended', 401, code, detail, { reasons })
  }
  if (response.status === 404) {
    return new ApiError(REQUEST_REFUSED, 'not_found', 404, code, detail, { reasons })
  }
  if (response.status >= 500) {
    return new ApiError(SERVER_TROUBLE, 'server', response.status, code, detail, { reasons })
  }
  return new ApiError(REQUEST_REFUSED, 'refused', response.status, code, detail, { reasons })
}

async function parse<T>(response: Response): Promise<T> {
  try {
    return await response.json() as T
  } catch (cause) {
    throw new ApiError(SERVER_TROUBLE, 'server', response.status, null, null, { cause })
  }
}

/**
 * Every data route goes through here, which is what makes the 401 mapping
 * single-sited. `login` and `me` deliberately do not: on those two routes a 401
 * is an answer rather than a failure, and each says so where it is written.
 */
async function request<T>(method: Method, path: string, query?: Query, body?: unknown): Promise<T> {
  const response = await send(method, path, query, body)
  if (!response.ok) throw await failureFor(response)
  return parse<T>(response)
}

const accountPath = (id: string): string => `/api/accounts/${encodeURIComponent(id)}`

/**
 * An `Overrides` object as the `?scenario=` parameter the one-pager route
 * takes — **base64url, unpadded**, byte for byte what
 * `encodeScenario` in `packages/server/src/http/routes/onepager.ts` produces
 * from the same object.
 *
 * It is written twice because the two halves cannot share a module: that one
 * imports Fastify and this one is bundled into a browser. So the agreement is
 * *tested* rather than assumed — `scripts/__tests__/onepager-scenario-link.test.ts`
 * imports both functions and asserts they answer identically over a set of
 * overrides including every key the route recognises. Padding is the specific
 * thing that would have differed: `btoa` pads with `=` and `Buffer`'s
 * `base64url` does not, and a padded parameter is a `%3D` in a URL a rep has
 * pasted into a new tab.
 *
 * `TextEncoder` rather than `btoa(json)` directly: `btoa` throws on any code
 * point above U+00FF, and an account whose scenario name carried one would
 * take the One-pager link out of the page rather than just mis-encode it.
 */
export function encodeScenario(overrides: ScenarioOverrides): string {
  const bytes = new TextEncoder().encode(JSON.stringify(overrides))
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * The one-pager's URL — **and R-18's export half** (F-3).
 *
 * The document opens in a new tab rather than being fetched and re-rendered, so
 * everything it must say has to be in this string. A rep with a what-if on
 * screen who clicked `One-pager` used to get the *estimate of record* on paper,
 * silently, while a red band overhead said otherwise: display rule 2 says a
 * scenario is visibly a scenario **in every exported document**, and the paper
 * is the one that ends up in a customer's hands.
 *
 * The overrides passed here are the route's own **echoed** ones
 * (`payload.scenario.overrides`), never the panel's draft, so a key the server
 * validated away cannot be described on paper as though it had applied.
 *
 * Every character base64url produces is already URL-safe, so the parameter is
 * appended rather than percent-encoded — which keeps the no-scenario URL
 * exactly the string it always was.
 */
export function onepagerPath(id: string, overrides: ScenarioOverrides | null): string {
  const path = `${accountPath(id)}/onepager`
  return overrides === null ? path : `${path}?scenario=${encodeScenario(overrides)}`
}
const listPath = (id: string): string => `/api/lists/${encodeURIComponent(id)}`

/**
 * Sign in.
 *
 * Returns a result rather than throwing, because a refused credential is a
 * normal outcome of a sign-in screen and not an exception — and because the
 * screen must render the *same* words for every refusal.
 *
 * **Every response that is not a success renders `CREDENTIALS_REJECTED`.** Not
 * "401 renders it": by class. The server sends 401 for all three credential
 * failures today, but a client that special-cased that one status would leak
 * the moment a future server answered, say, 403 for a deactivated account. The
 * only outcomes that read differently are a server that could not be reached
 * and a server that broke, neither of which is a statement about the account.
 *
 * There is no client-side email validation here, and none in the screen: a
 * browser that says "no account with that address" before a request goes out is
 * the enumeration oracle the server spends a dummy scrypt hash closing.
 */
async function login(email: string, password: string): Promise<LoginResult> {
  let response: Response
  try {
    response = await send('POST', '/api/auth/login', undefined, { email, password })
  } catch (error) {
    if (error instanceof ApiError && error.failure === 'network') {
      return { ok: false, message: NETWORK_UNREACHABLE }
    }
    throw error
  }

  if (response.ok) return { ok: true, user: await parse<User>(response) }
  if (response.status >= 500) return { ok: false, message: SERVER_TROUBLE }
  return { ok: false, message: CREDENTIALS_REJECTED }
}

/**
 * Who is signed in, or `null`.
 *
 * A 401 here is the answer, not a failure: it is what "nobody is signed in"
 * looks like on the wire, and it is the only way this package can find out —
 * the cookie is `HttpOnly`.
 */
async function me(): Promise<User | null> {
  const response = await send('GET', '/api/auth/me')
  if (response.status === 401) return null
  if (!response.ok) throw await failureFor(response)
  return parse<User>(response)
}

/**
 * Sign out. A 401 is success: the session this was asked to destroy is already
 * gone, which is the outcome the caller wanted.
 */
async function logout(): Promise<void> {
  const response = await send('POST', '/api/auth/logout')
  if (response.ok || response.status === 401) return
  throw await failureFor(response)
}

const accounts = (filters: AccountFilters = {}): Promise<AccountsPage> =>
  request<AccountsPage>('GET', '/api/accounts', filters)

const account = (id: string): Promise<AccountDetail> =>
  request<AccountDetail>('GET', accountPath(id))

/**
 * The same account scored for another day — the Sunrise Elementary contrast the
 * design brief calls the whole reason occupancy calendars exist (Task 8).
 *
 * Its own call and its own type, for the reason `presentation` is: the answer
 * is a *different envelope*, not an `AccountDetail` with an extra field. It
 * carries no characterization, no note and no stored estimate id, and typing it
 * as the drill-down's payload would hand a screen four fields that read
 * `undefined` while the compiler said they were there.
 *
 * `asOf` is required. There is no "recompute at no particular date".
 */
const recompute = (id: string, asOf: string): Promise<RecomputedPayload> =>
  request<RecomputedPayload>('GET', accountPath(id), { asOf })

/**
 * The customer-facing payload (R-28, spec §7.3) — the same route, a different
 * answer, and its own function.
 *
 * **This exists so that presentation mode cannot be built by filtering.** The
 * requirement is not that the screen declines to draw a note or a flag; it is
 * that neither ever arrives, because anything the browser received is one
 * devtools inspection or one screen-share away from the customer the laptop
 * has been turned towards. A component handed a `PresentationPayload` has
 * nothing to filter — the fields are not on the object and not on its type —
 * and one handed an `AccountDetail` would look identical on screen while
 * failing the requirement completely.
 *
 * It takes no options. `?asOf` is refused by the server in this mode (the
 * payload has no field for "not the estimate of record"), and a *second* mode
 * is not a thing this route has.
 */
const presentation = (id: string): Promise<PresentationPayload> =>
  request<PresentationPayload>('GET', accountPath(id), { mode: 'presentation' })

/**
 * One aspect of the working behind a figure (display rule 5).
 *
 * Generic in the aspect rather than returning a union: the caller has already
 * named which of the four it wants, so making it narrow the result afterwards
 * would be asking it to re-read its own argument. The four payload types are
 * transcribed from `routes/derivation.ts` — the first of this package's read
 * payloads to be typed in full, because this one is *consumed field by field*
 * (comparable days, factor rows, the hourly span) rather than passed through.
 */
const derivation = <A extends DerivationAspect>(id: string, aspect: A): Promise<DerivationByAspect[A]> =>
  request<DerivationByAspect[A]>('GET', `${accountPath(id)}/derivation/${aspect}`)

/**
 * R-17's what-if, computed live and never persisted.
 *
 * Typed in full for the same reason the derivations are: the panel that calls
 * it consumes the answer *field by field* — the nomination, the season split,
 * the profile the chart redraws, and `scenario.confidenceBasis`, which is the
 * whole reason the panel does not print a confidence label of its own.
 */
const scenario = (id: string, body: ScenarioRequest): Promise<ScenarioPayload> =>
  request<ScenarioPayload>('POST', `${accountPath(id)}/scenarios`, undefined, body)

/** Unwrapped from the route's `{ lists }` envelope so no screen has to know it was one. */
const lists = async (): Promise<SavedList[]> =>
  (await request<{ lists: SavedList[] }>('GET', '/api/lists')).lists

/** Answers the new list's id — the one thing the caller did not already know. */
const createList = async (name: string, accountIds?: string[]): Promise<string> => {
  const body: { name: string; accountIds?: string[] } = { name }
  if (accountIds !== undefined) body.accountIds = accountIds
  return (await request<{ id: string }>('POST', '/api/lists', undefined, body)).id
}

/** Answers the list's resulting state: `addToList` is idempotent, so the caller cannot compute it. */
const addToList = (listId: string, accountId: string): Promise<SavedList> =>
  request<SavedList>('POST', `${listPath(listId)}/accounts`, undefined, { accountId })

/** `null` clears the assignment, and is required — an absent key is a 400. */
const assignList = (listId: string, assignedToId: string | null): Promise<SavedList> =>
  request<SavedList>('PATCH', `${listPath(listId)}/assignment`, undefined, { assignedToId })

/**
 * The reps a list may be handed to.
 *
 * **A 403 here is an answer, not a breakage** — it is what "your role does not
 * get the roster" looks like on the wire, and the route is gated to
 * `program_manager`. It still throws, like every other data route, because a
 * screen that must not draw the assign control should decide that from the
 * caller's role rather than by firing a request it expects to be refused;
 * `ListsPanel` only calls this for a program manager.
 */
const assignableUsers = async (): Promise<AssignableUser[]> =>
  (await request<{ users: AssignableUser[] }>('GET', '/api/users/assignable')).users

/**
 * An account's whole note feed, newest first with the pinned note hoisted —
 * the server's ordering, not this package's.
 *
 * Unwrapped from `{ notes }` so no screen has to know it was an envelope, the
 * same as `lists`. There is no `limit` parameter here even though the route's
 * store takes one: the feed is the record, and a client-side page size would
 * decide which part of a record a rep is allowed to see.
 */
const notes = async (accountId: string): Promise<FeedNote[]> =>
  (await request<{ notes: FeedNote[] }>('GET', `${accountPath(accountId)}/notes`)).notes

/**
 * Writes a note. Answers the new note's id — the one thing the caller could not
 * already know.
 *
 * **Neither the author nor the timestamp is sent.** `routes/notes.ts` takes
 * `authorId` from the session and `createdAt` from the server's clock, and
 * rejects any unrecognised field, which is what makes R-27's "every note
 * carries its author and timestamp" a property of the system rather than of
 * whatever this function happened to put in the body.
 *
 * `tag` is `NoteTag | null` and required, not optional: "no tag" is a decision
 * a rep makes at the composer, and an omitted argument would make it the
 * default nobody chose.
 */
const createNote = async (accountId: string, body: string, tag: NoteTag | null): Promise<string> => {
  const payload: { body: string; tag?: NoteTag } = { body }
  // Absent, not `null`. The route reads an absent `tag` as "untagged"; on the
  // *edit* route the two are deliberately different requests, and sending the
  // wrong one there is the difference between an untouched tag and a cleared
  // one. Spelling "no tag" the same way on both routes keeps that distinction
  // learnable.
  if (tag !== null) payload.tag = tag
  return (await request<{ id: string }>('POST', `${accountPath(accountId)}/notes`, undefined, payload)).id
}

/**
 * A note's superseded versions, oldest first.
 *
 * **A 403 here is an answer, not a breakage** — `GET /api/notes/:id/history`
 * refuses a redacted note (the third of the five redaction rules), and the
 * history of a withheld body is the withheld body. It throws like every other
 * data route, and the caller is expected to say so on screen rather than to
 * show an empty history, which would claim the note had never been edited.
 */
const noteHistory = async (noteId: string): Promise<NoteRevision[]> => {
  const path = `/api/notes/${encodeURIComponent(noteId)}/history`
  return (await request<{ revisions: NoteRevision[] }>('GET', path)).revisions
}

/**
 * Notes across the whole book (R-29), newest first. No filters at all is every
 * non-redacted note, which is what makes "no notes have been written yet"
 * something the search screen can say rather than guess.
 *
 * **An empty string is dropped rather than sent.** `?text=` is a 400 on this
 * route — deliberately, because "absent" is spelled by leaving the key off and
 * `?text=` would otherwise match every note in the book — and a cleared search
 * box is exactly how a caller produces one. Dropping it here means the screen
 * can hold `''` for "nothing typed" without every clearing of the box being a
 * failed request. `buildUrl` already drops `undefined`; it does not drop `''`.
 */
const searchNotes = async (filters: NoteSearchFilters = {}): Promise<NoteSearchHit[]> => {
  const query: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value !== '') query[key] = value
  }
  return (await request<{ results: NoteSearchHit[] }>('GET', '/api/notes/search', query)).results
}

/**
 * The account's ranked strategy list (R-32), and the act of being shown it
 * (R-35).
 *
 * **This GET writes**, and the write is the point: the route records a
 * `strategy_presentations` row for every strategy it answers with, and stamps
 * the estimate with the library version they came from. There is no second
 * request in the interaction to hang that record on. Neither write touches
 * `input_digest` or `result_document`, so no figure moves — which is R-33 and
 * R-35 read together. A caller must therefore not fire this speculatively to
 * warm a cache; it is called when the surface is on screen.
 *
 * No `asOf` parameter. The route defaults it to the server's own day, and the
 * only surface that calls this shows the estimate of record.
 */
const strategies = (accountId: string): Promise<AccountRanking> =>
  request<AccountRanking>('GET', `${accountPath(accountId)}/strategies`)

/**
 * Records that a customer turned a strategy down, and answers with the end-use
 * exclusion that rejection **offers** (R-33).
 *
 * It does not apply it. Nothing on this path recomputes or persists an
 * estimate; the rep applies the exclusion explicitly through
 * `api.scenario`, where a scenario is visibly a scenario. That separation is
 * the seam the whole advisory boundary is drawn on, so a caller that fed this
 * answer straight into `api.scenario` without an interaction in between would
 * be the defect this function's shape exists to prevent.
 *
 * `reason` is required by the route and an empty or whitespace-only string is a
 * 400: "this rep rejected cooling setback" with no why answers none of the
 * questions the record exists for.
 */
const rejectStrategy = (
  accountId: string, strategyId: string, reason: string,
): Promise<StrategyRejection> =>
  request<StrategyRejection>(
    'POST',
    `${accountPath(accountId)}/strategies/${encodeURIComponent(strategyId)}/rejection`,
    undefined,
    { reason },
  )

/**
 * The strategy library in force today, whole (R-31) — the program's own copy,
 * not one account's ranking of it.
 *
 * `GET /api/strategies` is `requireAuth`, so this is not a program-manager
 * call; it is the same document `AccountRanking` was ranked out of, which is
 * why the console can list it beside the two weights it is ranked *by*.
 */
const strategyLibrary = (): Promise<StrategyLibraryInForce> =>
  request<StrategyLibraryInForce>('GET', '/api/strategies')

/**
 * A new immutable strategy library version (R-31, R-34 in part). **201**.
 *
 * `requireRole('program_manager')`, because committing one changes the ranked
 * list every rep is shown on every account — and there is **no preview route
 * for it**. `computeBlastRadius` scores estimates against a `ConfigDocument`;
 * a library commit moves no estimate and is not a `ConfigDocument`, so nothing
 * in this system can tell a program manager what a library commit would do
 * before they do it. The console states that where the button is rather than
 * leaving it to be discovered.
 *
 * There is deliberately no client-side validation before this call.
 * `validateStrategyLibrary` lives in the engine, this package does not depend
 * on it, and a second implementation in the browser would be a second answer to
 * a question the store already answers (R-17). A refusal comes back as a 400
 * whose message is the server's own list of reasons.
 */
const commitStrategyLibrary = (commit: StrategyLibraryCommit): Promise<StrategyLibraryCommitted> =>
  request<StrategyLibraryCommitted>('POST', '/api/strategies', undefined, commit)

/**
 * The configuration in force today (R-12), in the projection the server sends
 * every signed-in caller.
 *
 * **Typed in full**, unlike the large read payloads, because it is small, it is
 * a projection this repository defines on both sides of the wire, and both of
 * its consumers read it field by field: the as-of control takes
 * `season.start`/`season.end` as the picker's bounds, and the presentation
 * screen's assumptions line names the event count, the assumed duration and
 * both rates.
 *
 * No `?date=` parameter. "In force today" is the server's own day; a
 * caller-supplied date is `GET /api/config/at`, which is gated to a program
 * manager because it answers with the whole document.
 */
const config = (): Promise<CurrentConfig> => request<CurrentConfig>('GET', '/api/config/current')

/**
 * The whole configuration version in force on `date` (R-22) — **program
 * manager only**, because it answers with the program's internal model rather
 * than the one-pager projection {@link config} serves.
 *
 * This is the console's starting point as well as its time-travel control. An
 * edit is an edit *of* something, and the only honest something is the document
 * the server is actually running; a client-side default would be a second copy
 * of `config/defaults.ts` with nothing keeping the two equal.
 *
 * A date before the first version is a **404**, not the earliest version.
 * `resolveConfigAt` refuses to name a version as having been in force before it
 * existed, and that refusal survives into this client as an `ApiError` with
 * `failure: 'not_found'` for a screen to render as a sentence.
 */
const configAt = (date: string): Promise<ConfigVersion> =>
  request<ConfigVersion>('GET', '/api/config/at', { date })

/** This program manager's saved draft, its diff against what is in force, and why it would be refused. */
const configDraft = (): Promise<ConfigDraftView> =>
  request<ConfigDraftView>('GET', '/api/config/draft')

/**
 * Replaces this program manager's draft, whole.
 *
 * `PATCH` rather than `PUT` although it replaces: {@link Method} has no `'PUT'`
 * — this API does not use one anywhere — and the route was written `PATCH` for
 * exactly that reason. It is not a partial update.
 *
 * **Saving does not validate.** A half-finished rate table has to be something a
 * program manager can put down and come back to, so the server stores whatever
 * it is handed and reports the reasons it could not be committed in `errors`
 * alongside it. `commitConfig` is the gate.
 */
const saveConfigDraft = (document: ConfigDocument): Promise<ConfigDraftView> =>
  request<ConfigDraftView>('PATCH', '/api/config/draft', undefined, { document })

/**
 * What committing this candidate would change (R-13) — **a POST that persists
 * nothing.** It is a POST because it carries a document, not because it writes:
 * no estimate, no run, no `config_versions` row.
 *
 * `limit` is required here although the route makes it optional, and that is
 * this client's own rule rather than the server's. Omitted, the server scores
 * every in-scope account — 38.3 s over the seeded book, measured — which is not
 * a thing a form can wait for. The route refuses to default it so that no
 * caller is ever handed a sample it did not ask for; requiring it here means no
 * screen can ask for the 40-second answer by forgetting a field.
 */
const configBlastRadius = (
  args: { document: ConfigDocument; limit: number; asOfDate?: string },
): Promise<BlastRadiusPreview> =>
  request<BlastRadiusPreview>('POST', '/api/config/draft/blast-radius', undefined, {
    document: args.document,
    limit: args.limit,
    // Assigned rather than spelled `asOfDate: undefined`: the route rejects an
    // unrecognised field, and while `JSON.stringify` would drop the key anyway,
    // `exactOptionalPropertyTypes` makes the difference a real one at the type
    // level and this is where it is spelled.
    ...(args.asOfDate === undefined ? {} : { asOfDate: args.asOfDate }),
  })

/**
 * A new immutable configuration version (R-12). **201**, not 200.
 *
 * `committedBy` and `committedAt` are not in this signature because they are
 * not in the body: the server takes the first from the session and the second
 * from its own clock, so that nobody can commit as somebody else or backdate a
 * commit past the tiebreak `resolveConfigAt` orders by.
 *
 * `effectiveFrom` is required, which is R-12's whole point — nothing is
 * overwritten, and a version with no effective date would have nothing to be
 * effective *from*.
 */
const commitConfig = (commit: ConfigCommit): Promise<ConfigCommitted> =>
  request<ConfigCommitted>('POST', '/api/config/commit', undefined, commit)

const characterizationPath = (id: string): string =>
  `/api/characterizations/${encodeURIComponent(id)}`

/**
 * The proposals waiting on an analyst (R-24), oldest first — and **no impacts**.
 *
 * `requireRole('analyst')`, so every other role's session gets a 403 here; the
 * app bar draws the tab as a disabled control for them rather than sending them
 * into it, and the screen still reads the 403 as a sentence for anyone who
 * typed the URL.
 *
 * The route sends rows and nothing else on purpose. An impact preview is two
 * `scoreAccount` calls — **207 ms, measured** — so previewing the wireframe's
 * 31 waiting rows eagerly is ~6.4 s of synchronous work before the list can
 * render. {@link characterizationImpact} is the per-selection call.
 *
 * Unwrapped out of `{ queue }` here, so no screen repeats the envelope key.
 */
const reviewQueue = (): Promise<CharacterizationQueueRow[]> =>
  request<{ queue: CharacterizationQueueRow[] }>('GET', '/api/characterizations/review')
    .then((body) => body.queue)

/**
 * What approving this proposal would do to the account's estimate (R-25),
 * **persisting nothing** — a GET, because it writes nothing at all.
 *
 * One selection at a time. See {@link reviewQueue} for the budget this splits.
 */
const characterizationImpact = (id: string): Promise<CharacterizationImpact> =>
  request<CharacterizationImpact>('GET', `${characterizationPath(id)}/impact`)

/**
 * Approve, amend or reject (R-25) — one route, because they are one action with
 * three outcomes.
 *
 * `reviewedBy` and `reviewedAt` are not in this signature because they are not
 * in the body: the server takes the first from the session and the second from
 * its own clock, for the reason `commitConfig` gives about `committedBy`.
 *
 * Absent keys rather than present `undefined`s — the route refuses an
 * unrecognised field, and `exactOptionalPropertyTypes` makes the difference a
 * real one at the type level.
 */
const decideCharacterization = (id: string, review: ReviewRequest): Promise<ReviewOutcome> =>
  request<ReviewOutcome>('POST', `${characterizationPath(id)}/review`, undefined, {
    decision: review.decision,
    ...(review.reason === undefined ? {} : { reason: review.reason }),
    ...(review.buildingType === undefined ? {} : { buildingType: review.buildingType }),
  })

/**
 * One object rather than a bag of named exports, so a screen importing it reads
 * as `api.accounts(...)` — and so that "who is calling the server" is greppable
 * in one word.
 */
export const api = {
  login, logout, me, accounts, account, recompute, presentation, derivation, scenario,
  lists, createList, addToList, assignList, assignableUsers,
  notes, createNote, noteHistory, searchNotes,
  strategies, rejectStrategy, strategyLibrary, commitStrategyLibrary,
  config, configAt, configDraft, saveConfigDraft, configBlastRadius, commitConfig,
  reviewQueue, characterizationImpact, decideCharacterization,
} as const
